"use client";

import { useChat } from "@ai-sdk/react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { useMutation } from "@urql/next";
import { APICallError, DefaultChatTransport, isToolUIPart } from "ai";
import { nanoid } from "nanoid";
import { usePathname, useSearchParams } from "next/navigation";
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useTrackEvent } from "@/components/analytics/events";
import { buildAnalysisScope } from "@/lib/ai/analysis-scope";
import { type AnalysisTarget } from "@/lib/ai/analysis-target";
import { applyToolApprovalResponse } from "@/lib/ai/apply-tool-approval-response";
import {
  ChatAppError,
  createChatTransportError,
} from "@/lib/ai/chat-app-error";
import {
  CHAT_ATTACHMENT_MAX_COUNT,
  stripTransientFilePartsFromMessages,
} from "@/lib/ai/chat-attachments";
import { useChatClientPlugins } from "@/lib/ai/chat-client-plugin";
import {
  buildChatClientRequestBody,
  CHAT_STREAM_MESSAGE_THROTTLE_MS,
  handleChatClientPluginDataPart,
  shouldSuppressChatClientError,
  useChatClientPluginThreadScopeChange,
} from "@/lib/ai/chat-client-runtime";
import {
  type ChatExternalErrorState,
  isNetworkTransportError,
  resolveChatExternalError,
} from "@/lib/ai/chat-external-error";
import { filterEmptyAssistantMessages } from "@/lib/ai/chat-history";
import { uiMessageVercelToGql } from "@/lib/ai/convert/to-graphql";
import { uiMessageGqlToVercel } from "@/lib/ai/convert/to-vercel";
import {
  type CurrentPageContext,
  isCurrentPageAnalysisScopeEligible,
} from "@/lib/ai/current-page-context";
import {
  type AiModelAccess,
  isRestrictedHighTierModel,
} from "@/lib/ai/model-access";
import { type ModelId } from "@/lib/ai/models";
import {
  AppendAgentChatMessages,
  CreateAgentChat,
  UpdateAgentChatMessage,
} from "@/lib/ai/mutation";
import { getLatestTokenUsage } from "@/lib/ai/token-usage";
import { getToolConfirmRegistry } from "@/lib/ai/tools/ui";
import type { AgentUIMessage } from "@/lib/ai/types";
import { logger } from "@/lib/logger";
import {
  type AnalysisTargetMentionInstance,
  getAnalysisTargetMentionInputs,
  getMentionedAnalysisTargets,
} from "./analysis-target-mention-token";
import {
  hasPendingToolApproval,
  isApprovalContinuationReady,
} from "./approval-continuation";
import { buildChatMessageParts, type ChatSendInput } from "./chat-send-input";
import { createInitialThreadState, threadReducer } from "./chat-thread-reducer";
import { useInitialMessageSync } from "./use-initial-message-sync";
import {
  isQueueInvalidationError,
  type PersistenceGenerationToken,
  usePersistenceQueue,
} from "./use-persistence-queue";

// --- 型定義 ---
type BuildThreadRequestContextParams = {
  text?: string;
  mentions?: AnalysisTargetMentionInstance[];
};

type UseChatThreadProps = {
  workspaceId: string;
  initialChatId?: string | null;
  activeChatId?: string | null;
  initialMessages: AgentUIMessage[];
  model: ModelId;
  aiModelAccess: AiModelAccess;
  pageAnalysisTarget: AnalysisTarget | null;
  manualAnalysisTargets: AnalysisTarget[];
  currentPageContext: CurrentPageContext | null;
  isCreatedByViewer?: boolean;
  onCreate: (chatId: string) => void;
};

type ApprovalContinuationWait = {
  token: PersistenceGenerationToken;
  promise: Promise<void>;
  started: boolean;
  resolve: () => void;
  reject: (error: unknown) => void;
};

// --- 補助関数 ---

// legacy confirmation は native approval と異なる応答経路を持つため、移行期間中だけ
// continuation の公式完了条件から除外する。旧 registry の削除時にこの集合も不要になる。
const LEGACY_CONFIRMATION_TOOL_NAMES = new Set(
  Object.keys(getToolConfirmRegistry()),
);

function createApprovalContinuationWait(
  token: PersistenceGenerationToken,
): ApprovalContinuationWait {
  let resolve: () => void = () => undefined;
  let reject: (error: unknown) => void = () => undefined;
  const promise = new Promise<void>((resolvePromise, rejectPromise) => {
    resolve = resolvePromise;
    reject = rejectPromise;
  });
  return { token, promise, started: false, resolve, reject };
}

function getClientMessageIdFromMetadata(metadata: unknown): string | undefined {
  if (typeof metadata !== "object" || metadata === null) return undefined;
  const candidate = (metadata as Record<string, unknown>).clientMessageId;
  return typeof candidate === "string" && candidate.length > 0
    ? candidate
    : undefined;
}

function getClientMessageId(message: AgentUIMessage): string {
  const clientMessageId = getClientMessageIdFromMetadata(message.metadata);
  if (clientMessageId) return clientMessageId;
  if (message.id) {
    return message.id;
  }
  return nanoid();
}

function withClientMessageIds(messages: AgentUIMessage[]) {
  let changed = false;
  const normalized = messages.map((m) => {
    const clientMessageId = getClientMessageId(m);
    const metadata = (m.metadata as Record<string, unknown> | undefined) ?? {};
    if (metadata.clientMessageId === clientMessageId) return m;
    changed = true;
    return {
      ...m,
      metadata: {
        ...metadata,
        clientMessageId,
      },
    } satisfies AgentUIMessage;
  });
  return changed ? normalized : messages;
}

/**
 * 明示停止した最新turnで、内容が生成されたassistant messageだけに終了理由を付ける。
 */
function markLatestAssistantAsUserStopped(
  messages: AgentUIMessage[],
): AgentUIMessage[] {
  let latestUserIndex = -1;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index]?.role === "user") {
      latestUserIndex = index;
      break;
    }
  }
  if (latestUserIndex < 0) return messages;

  let latestAssistantIndex = -1;
  for (let index = messages.length - 1; index > latestUserIndex; index -= 1) {
    const message = messages[index];
    if (message?.role === "assistant" && message.parts.length > 0) {
      latestAssistantIndex = index;
      break;
    }
  }
  if (latestAssistantIndex < 0) return messages;

  return messages.map((message, index) =>
    index === latestAssistantIndex
      ? {
          ...message,
          metadata: {
            ...(message.metadata ?? {}),
            completionCause: "user-stopped",
          },
        }
      : message,
  );
}

function snapshotMessage(message: AgentUIMessage): string {
  const { id, role, metadata, parts } = message;
  return JSON.stringify({ id, role, metadata, parts });
}

function buildMessagesSignature(messages: AgentUIMessage[]): string {
  return messages.map((message) => snapshotMessage(message)).join(",");
}

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

function isAbortLikeError(error: Error): boolean {
  const normalizedName = error.name.toLowerCase();
  const normalizedMessage = error.message.toLowerCase();
  return (
    normalizedName === "aborterror" ||
    normalizedMessage.includes("abort") ||
    normalizedMessage.includes("cancel")
  );
}

type PersistenceOperation = "createAgentChat" | "updateAgentChat";

class ChatPersistenceError extends Error {
  readonly operation: PersistenceOperation;

  constructor(operation: PersistenceOperation, cause: Error) {
    super(cause.message);
    this.name = "ChatPersistenceError";
    this.operation = operation;
    this.cause = cause;
  }
}

function getPersistenceOperation(
  error: unknown,
): PersistenceOperation | undefined {
  return error instanceof ChatPersistenceError ? error.operation : undefined;
}

function buildPersistenceTracking(messages: AgentUIMessage[]) {
  const messageIds = new Set(messages.map(getClientMessageId));
  const serverIdMap = new Map(
    messages
      .map((m) => {
        const clientId = getClientMessageId(m);
        return m.id ? [clientId, m.id] : null;
      })
      .filter((v): v is [string, string] => Boolean(v)),
  );
  const snapshots = new Map(
    messages.map((m) => [getClientMessageId(m), snapshotMessage(m)]),
  );
  return { messageIds, serverIdMap, snapshots };
}

function mergeCompletedToolReceipts(
  persistedMessages: AgentUIMessage[],
  currentMessages: AgentUIMessage[],
) {
  const currentMessagesByClientId = new Map(
    currentMessages.map((message) => [getClientMessageId(message), message]),
  );
  const changedClientMessageIds = new Set<string>();

  const mergedMessages = persistedMessages.map((persistedMessage) => {
    const clientMessageId = getClientMessageId(persistedMessage);
    const currentMessage = currentMessagesByClientId.get(clientMessageId);
    if (!currentMessage) return persistedMessage;

    const completedToolPartsByCallId = new Map(
      currentMessage.parts
        .filter(
          (part) =>
            isToolUIPart(part) &&
            (part.state === "output-available" ||
              part.state === "output-error" ||
              part.state === "output-denied"),
        )
        .map((part) => [part.toolCallId, part]),
    );
    let messageChanged = false;
    const mergedParts = persistedMessage.parts.map((persistedPart) => {
      if (!isToolUIPart(persistedPart)) return persistedPart;
      const completedPart = completedToolPartsByCallId.get(
        persistedPart.toolCallId,
      );
      if (
        !completedPart ||
        JSON.stringify(completedPart) === JSON.stringify(persistedPart)
      ) {
        return persistedPart;
      }

      messageChanged = true;
      return completedPart;
    });

    if (!messageChanged) return persistedMessage;
    changedClientMessageIds.add(clientMessageId);
    return { ...persistedMessage, parts: mergedParts };
  });

  return { mergedMessages, changedClientMessageIds };
}

/**
 * チャット送受信と永続化を管理し、client plugin host への接続点もまとめて扱う。
 *
 * @remarks
 * thread 本体は chat の基礎フローに集中し、request body 拡張・stream event・
 * error policy などの機能差分は `ChatClientPlugin` 群へ委譲する。
 */
export function useChatThread({
  workspaceId,
  activeChatId: propActiveChatId,
  initialMessages: propInitialMessages,
  model,
  aiModelAccess,
  pageAnalysisTarget,
  manualAnalysisTargets,
  currentPageContext,
  isCreatedByViewer,
  onCreate,
}: UseChatThreadProps) {
  const trackEvent = useTrackEvent();
  const { i18n } = useLingui();
  const isEditable = isCreatedByViewer !== false;
  const chatClientPlugins = useChatClientPlugins();
  const [isStartingNewChat, setIsStartingNewChat] = useState(false);

  // 永続化 mutation
  const [, createMutation] = useMutation(CreateAgentChat);
  const [, appendMutation] = useMutation(AppendAgentChatMessages);
  const [, updateMessageMutation] = useMutation(UpdateAgentChatMessage);

  // thread 状態の初期化
  const [state, dispatch] = useReducer(
    threadReducer,
    propActiveChatId,
    createInitialThreadState,
  );

  // reducer 状態から導出する識別子
  const isCreating = state.status === "persisting";
  const persistedChatId =
    state.status === "persisted" ? state.persistedChatId : null;
  // 初回保存は同じ会話の昇格なので、draft IDを維持してSDKの履歴を再初期化しない。
  const chatInstanceId =
    state.status === "persisted"
      ? (state.draftChatId ?? state.persistedChatId)
      : state.draftChatId;
  const threadScopeId =
    state.status === "persisted"
      ? (state.draftChatId ?? state.persistedChatId)
      : state.draftChatId;

  // navigation や新規作成に伴う props の変更を reducer へ同期する。
  // reducer 側の同値判定だけに頼らず、ref でも実際の変更を判定して不要な dispatch を防ぐ。
  const lastPropId = useRef(propActiveChatId);
  const pendingCreatedIdRef = useRef<string | null>(null);
  const startNewChatContextRef = useRef<{
    chatId: string | null;
    messageSignature: string;
  } | null>(null);
  useEffect(() => {
    // props が変わった場合だけ同期し、effect と reducer の往復を発生させない。
    if (propActiveChatId !== lastPropId.current) {
      if (
        propActiveChatId &&
        propActiveChatId === pendingCreatedIdRef.current
      ) {
        // 直前に作成した chat の URL 反映では、会話スコープを表す draftChatId を維持する。
        dispatch({ type: "CREATED", newId: propActiveChatId });
        pendingCreatedIdRef.current = null;
      } else {
        dispatch({ type: "SYNC_PROP", chatId: propActiveChatId ?? null });
        if (pendingCreatedIdRef.current) {
          pendingCreatedIdRef.current = null;
        }
      }
      lastPropId.current = propActiveChatId;
    }
  }, [propActiveChatId]);

  // client message ID を補った初期メッセージ
  const effectiveInitialMessages = useMemo(() => {
    return withClientMessageIds(propInitialMessages);
  }, [propInitialMessages]);

  useEffect(() => {
    const resetContext = startNewChatContextRef.current;
    if (!isStartingNewChat || !resetContext) {
      return;
    }
    const currentSignature = buildMessagesSignature(effectiveInitialMessages);
    const hasExternalContextChanged =
      resetContext.chatId !== (propActiveChatId ?? null) ||
      resetContext.messageSignature !== currentSignature;

    if (hasExternalContextChanged) {
      startNewChatContextRef.current = null;
      setIsStartingNewChat(false);
    }
  }, [effectiveInitialMessages, isStartingNewChat, propActiveChatId]);

  // client ID と server ID、および保存済み snapshot の対応を追跡する。
  const initialTracking = buildPersistenceTracking(effectiveInitialMessages);
  const persistedMessageIds = useRef<Set<string>>(initialTracking.messageIds);
  const serverMessageIdMap = useRef<Map<string, string>>(
    initialTracking.serverIdMap,
  );
  const persistedSnapshots = useRef<Map<string, string>>(
    initialTracking.snapshots,
  );
  const latestPersistedMessagesRef = useRef<AgentUIMessage[]>(
    effectiveInitialMessages,
  );
  const resetPersistenceTracking = useCallback((messages: AgentUIMessage[]) => {
    const nextTracking = buildPersistenceTracking(messages);
    persistedMessageIds.current = nextTracking.messageIds;
    serverMessageIdMap.current = nextTracking.serverIdMap;
    persistedSnapshots.current = nextTracking.snapshots;
    latestPersistedMessagesRef.current = messages;
  }, []);

  const [error, setError] = useState<string | null>(null);
  const [appError, setAppError] = useState<ChatAppError | null>(null);
  const [externalError, setExternalError] =
    useState<ChatExternalErrorState | null>(null);
  const [isRetryingApprovalContinuation, setIsRetryingApprovalContinuation] =
    useState(false);
  const approvalContinuationRetryRef = useRef(false);
  const approvalContinuationRef = useRef<ApprovalContinuationWait | null>(null);
  const clearError = useCallback(() => {
    setError(null);
    setAppError(null);
    setExternalError(null);
  }, []);
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const currentUrl = useMemo(() => {
    if (typeof window === "undefined") return undefined;
    const query = searchParams?.toString();
    return window.location.origin + pathname + (query ? `?${query}` : "");
  }, [pathname, searchParams]);

  const langFromPath = useMemo(() => {
    const [first] = pathname.split("/").filter(Boolean);
    return first ?? null;
  }, [pathname]);
  const previousAppErrorResetKeyRef = useRef(`${workspaceId}:${model}`);

  useEffect(() => {
    // model を切り替えた後は前回送信由来の quota / plan error を引きずらない。
    const nextKey = `${workspaceId}:${model}`;
    if (previousAppErrorResetKeyRef.current === nextKey) {
      return;
    }
    previousAppErrorResetKeyRef.current = nextKey;
    setAppError(null);
    setExternalError(null);
  }, [model, workspaceId]);

  let chatUrl: string | undefined;
  if (langFromPath) {
    const encodedWorkspaceId = encodeURIComponent(workspaceId);
    const chatId = persistedChatId ?? propActiveChatId ?? null;
    const chatPath = chatId
      ? `/${langFromPath}/${encodedWorkspaceId}/ai/${encodeURIComponent(chatId)}`
      : `/${langFromPath}/${encodedWorkspaceId}/ai`;
    chatUrl =
      typeof window === "undefined"
        ? chatPath
        : window.location.origin + chatPath;
  }

  const logContext = useMemo(
    () => ({
      workspaceId,
      chatId: persistedChatId ?? propActiveChatId ?? null,
      draftChatId: persistedChatId ? null : chatInstanceId,
      model,
      currentUrl,
      chatUrl,
    }),
    [
      workspaceId,
      persistedChatId,
      propActiveChatId,
      chatInstanceId,
      model,
      currentUrl,
      chatUrl,
    ],
  );

  /**
   * 現在の thread state から request-scoped AI context を再構築する。
   *
   * 契約:
   * - bare `sendMessage()` でも page/manual/current-page context は最新 state を引き継ぐ。
   * - 明示 `@mention` がある通常送信だけ、`text` と `mentions` から mention binding を上乗せする。
   */
  const buildThreadRequestContextBody = useCallback(
    (params: BuildThreadRequestContextParams = {}) => {
      const text = params.text ?? "";
      const mentions = params.mentions ?? [];
      const mentionInputs =
        text.length > 0 && mentions.length > 0
          ? getAnalysisTargetMentionInputs(text, mentions)
          : [];
      const mentionedTargets =
        mentions.length > 0 ? getMentionedAnalysisTargets(mentions) : [];

      const analysisScope = buildAnalysisScope({
        pageTarget: pageAnalysisTarget,
        pageContextEnabled: pageAnalysisTarget !== null,
        manualTargets: manualAnalysisTargets,
        mentionedTargets,
        mentionInputs,
      });
      const requestCurrentPageContext =
        currentPageContext &&
        (pageAnalysisTarget !== null ||
          !isCurrentPageAnalysisScopeEligible(currentPageContext.pageKind))
          ? currentPageContext
          : null;

      // Session Explorer の表示対象だけを渡し、モデル設定など他の URL state は含めない。
      if (requestCurrentPageContext?.pageKind === "ua_session_explorer") {
        const sessionQuery: NonNullable<CurrentPageContext["sessionQuery"]> =
          {};
        for (const key of ["gaSessionId", "userPseudoId"] as const) {
          const value = searchParams?.get(key);
          if (value !== null && value !== undefined) sessionQuery[key] = value;
        }
        return {
          analysisScope,
          currentPageContext: { ...requestCurrentPageContext, sessionQuery },
        };
      }

      return {
        analysisScope,
        ...(requestCurrentPageContext
          ? { currentPageContext: requestCurrentPageContext }
          : {}),
      };
    },
    [
      currentPageContext,
      manualAnalysisTargets,
      pageAnalysisTarget,
      searchParams,
    ],
  );

  const buildRequestBody = useCallback(
    (chatId?: string | null, params: BuildThreadRequestContextParams = {}) => ({
      ...buildChatClientRequestBody({
        workspaceId,
        model,
        chatId,
        lang: langFromPath ?? undefined,
        currentUrl,
        plugins: chatClientPlugins,
      }),
      ...buildThreadRequestContextBody(params),
    }),
    [
      workspaceId,
      model,
      langFromPath,
      currentUrl,
      chatClientPlugins,
      buildThreadRequestContextBody,
    ],
  );

  const transport = useMemo(() => {
    return new DefaultChatTransport({
      api: "/api/chat",
      body: buildRequestBody(persistedChatId),
      fetch: async (input, init) => {
        const response = await fetch(input, init);
        if (response.ok) {
          return response;
        }

        throw await createChatTransportError(response);
      },
    });
  }, [buildRequestBody, persistedChatId]);

  const latestMessagesRef = useRef<AgentUIMessage[]>(effectiveInitialMessages);
  const seedMessages = isStartingNewChat ? [] : effectiveInitialMessages;
  // SDKの会話は維持しても、保存先が確定した後はdraft世代の未実行sendを失効させる。
  const persistenceContextKey = persistedChatId ?? chatInstanceId;
  const {
    createGenerationToken,
    pendingCount: pendingPersistenceCount,
    resolveNextWait,
    rejectNextWait,
    hasPendingWaitForToken,
    markContextTransition,
    invalidateQueue,
    runSerialized,
  } = usePersistenceQueue(persistenceContextKey);
  const callbackGenerationToken = useMemo(
    () => createGenerationToken(persistenceContextKey),
    [createGenerationToken, persistenceContextKey],
  );
  const stopRequestedTokenRef = useRef<PersistenceGenerationToken | null>(null);

  const isSameGenerationToken = useCallback(
    (
      left: PersistenceGenerationToken | null,
      right: PersistenceGenerationToken,
    ) => {
      if (!left) return false;
      return (
        left.contextKey === right.contextKey &&
        left.generation === right.generation
      );
    },
    [],
  );

  const clearStopRequestForToken = useCallback(
    (token: PersistenceGenerationToken) => {
      if (isSameGenerationToken(stopRequestedTokenRef.current, token)) {
        stopRequestedTokenRef.current = null;
      }
    },
    [isSameGenerationToken],
  );

  useChatClientPluginThreadScopeChange(threadScopeId, chatClientPlugins);

  // AI SDK の chat hook
  const {
    messages,
    sendMessage,
    status,
    stop: rawStop,
    addToolOutput,
    addToolApprovalResponse,
    setMessages,
  } = useChat<AgentUIMessage>({
    id: chatInstanceId,
    messages: seedMessages,
    // 1 frame ぶんだけ store 通知をまとめ、stream 中の外部 store 再描画圧を抑える。
    throttle: CHAT_STREAM_MESSAGE_THROTTLE_MS,
    transport,
    onError: (e) => {
      if (
        isAbortLikeError(e) &&
        isSameGenerationToken(
          stopRequestedTokenRef.current,
          callbackGenerationToken,
        )
      ) {
        // AI SDK はこの直後の onFinish へ throttle 前の live messages を渡す。
        // 停止 token をここで消費すると、その完全な snapshot を保存できなくなる。
        return;
      }
      clearStopRequestForToken(callbackGenerationToken);
      const suppressError = shouldSuppressChatClientError(chatClientPlugins, e);
      const isAppError = ChatAppError.isInstance(e);

      if (!suppressError) {
        if (isAppError) {
          logger.info("AI chat app error", {
            ...logContext,
            source: "ai-chat",
            code: e.code,
            message: e.message,
          });
        } else {
          const resolvedExternalError = resolveChatExternalError(e);
          if (resolvedExternalError.kind !== "generic") {
            logger.warn("AI chat external error", {
              ...logContext,
              source: "ai-chat",
              kind: resolvedExternalError.kind,
              message: resolvedExternalError.rawMessage,
            });
          } else {
            logger.error(e, {
              ...logContext,
              source: "ai-chat",
            });
          }
        }
      }

      if (!suppressError) {
        if (isAppError) {
          setAppError(e);
          setExternalError(null);
          setError(null);
        } else {
          const resolvedExternalError = resolveChatExternalError(e);
          setAppError(null);
          setExternalError(resolvedExternalError);
          setError(null);
        }
      }
      dispatch({ type: "ERROR" });
      void rollbackFailedStream(latestMessagesRef.current).finally(() => {
        rejectNextWait(callbackGenerationToken, e);
      });
    },
    onData: (part) => {
      handleChatClientPluginDataPart(
        chatClientPlugins,
        part as {
          type: string;
          data?: unknown;
        },
      );
    },
    onFinish: async ({
      messages: latestMessages,
      isAbort,
      isDisconnect,
      isError,
    }) => {
      if (isAbort || isDisconnect || isError) {
        if (
          isAbort &&
          isSameGenerationToken(
            stopRequestedTokenRef.current,
            callbackGenerationToken,
          )
        ) {
          if (!hasPendingWaitForToken(callbackGenerationToken)) {
            clearStopRequestForToken(callbackGenerationToken);
            return;
          }
          await persistStoppedStreamAndSettleWait(
            latestMessages,
            callbackGenerationToken,
          );
          return;
        }
        clearStopRequestForToken(callbackGenerationToken);
        await rollbackFailedStream(latestMessages);

        let failureMessage = "Chat request failed";
        if (isAbort) {
          failureMessage = "Chat request was aborted";
        } else if (isDisconnect) {
          failureMessage = "Chat request was disconnected";
        }

        rejectNextWait(callbackGenerationToken, new Error(failureMessage));
        return;
      }
      clearStopRequestForToken(callbackGenerationToken);
      try {
        // 1. 空メッセージと一時的な file part を除き、保存形式へ正規化する。
        const persistableLatestMessages =
          filterEmptyAssistantMessages(latestMessages);
        const normalized = stripTransientFilePartsFromMessages(
          withClientMessageIds(persistableLatestMessages),
        );
        setMessages(normalized);
        const hasPersistableMessage = normalized.some(
          (normalizedMessage) => normalizedMessage.role !== "system",
        );
        if (!hasPersistableMessage) {
          resolveNextWait(callbackGenerationToken);
          return;
        }

        // 2. 新規作成または差分更新として永続化する。
        await handlePersistence(normalized);
        resolveNextWait(callbackGenerationToken);
      } catch (persistenceError) {
        const normalizedError = toError(persistenceError);
        const operation = getPersistenceOperation(normalizedError);
        logger.error(normalizedError, {
          ...logContext,
          source: "ai-chat",
          ...(operation ? { operation } : {}),
        });
        setExternalError(null);
        setError(normalizedError.message);
        dispatch({ type: "ERROR" });
        rejectNextWait(callbackGenerationToken, normalizedError);
      }
    },
  });

  /**
   * 失敗した turn を保存済み境界へ戻し、同じ turn 内で得た Tool の完了結果だけを保存する。
   */
  async function rollbackFailedStream(currentMessages: AgentUIMessage[]) {
    const { mergedMessages, changedClientMessageIds } =
      mergeCompletedToolReceipts(
        latestPersistedMessagesRef.current,
        currentMessages,
      );

    setMessages(latestPersistedMessagesRef.current);

    if (!persistedChatId || changedClientMessageIds.size === 0) return;

    try {
      const pendingReceiptUpdates: Array<{
        clientMessageId: string;
        message: AgentUIMessage;
        serverMessageId: string;
      }> = [];

      for (const message of mergedMessages) {
        const clientMessageId = getClientMessageId(message);
        if (!changedClientMessageIds.has(clientMessageId)) continue;

        const serverMessageId = serverMessageIdMap.current.get(clientMessageId);
        if (!serverMessageId) {
          throw new Error(
            `Missing server message ID for completed tool receipt: ${clientMessageId}`,
          );
        }
        pendingReceiptUpdates.push({
          clientMessageId,
          message,
          serverMessageId,
        });
      }

      for (const { message, serverMessageId } of pendingReceiptUpdates) {
        const persistableMessage =
          stripTransientFilePartsFromMessages([message])[0] ?? message;
        const res = await updateMessageMutation({
          input: {
            id: serverMessageId,
            message: uiMessageVercelToGql(persistableMessage),
          },
        });
        if (res.error) throw new Error(res.error.message);
      }

      for (const { clientMessageId, message } of pendingReceiptUpdates) {
        persistedSnapshots.current.set(
          clientMessageId,
          snapshotMessage(message),
        );
      }
      latestPersistedMessagesRef.current = mergedMessages;
      setMessages(mergedMessages);
    } catch (persistenceError) {
      const normalizedError = toError(persistenceError);
      const chatPersistenceError = new ChatPersistenceError(
        "updateAgentChat",
        normalizedError,
      );
      logger.error(chatPersistenceError, {
        ...logContext,
        source: "ai-chat",
        operation: chatPersistenceError.operation,
      });
      setError(chatPersistenceError.message);
      dispatch({ type: "ERROR" });
    }
  }

  const latestSendMessageRef = useRef(sendMessage);
  useEffect(() => {
    latestSendMessageRef.current = sendMessage;
  }, [sendMessage]);
  const latestAddToolApprovalResponseRef = useRef(addToolApprovalResponse);
  useEffect(() => {
    latestAddToolApprovalResponseRef.current = addToolApprovalResponse;
  }, [addToolApprovalResponse]);
  const latestAddToolOutputRef = useRef(addToolOutput);
  useEffect(() => {
    latestAddToolOutputRef.current = addToolOutput;
  }, [addToolOutput]);
  const latestI18nRef = useRef(i18n);
  useEffect(() => {
    latestI18nRef.current = i18n;
  }, [i18n]);

  const stop = useCallback(() => {
    stopRequestedTokenRef.current = callbackGenerationToken;
    rawStop();
  }, [callbackGenerationToken, rawStop]);

  /**
   * `useChat` は transport が変わっても chat instance を作り直さないため、
   * route / scope / plugin 依存の request body は送信時点の最新値で毎回上書きする。
   */
  const buildLatestRequestOptions = useCallback(
    (options: Parameters<typeof sendMessage>[1]) => {
      const requestBody = buildRequestBody(persistedChatId);
      const currentBody =
        options?.body && typeof options.body === "object" ? options.body : {};

      return {
        ...options,
        body: {
          ...requestBody,
          ...currentBody,
        },
      };
    },
    [buildRequestBody, persistedChatId],
  );

  useEffect(() => {
    latestMessagesRef.current = messages;
  }, [messages]);

  useInitialMessageSync({
    activeChatId: propActiveChatId,
    pendingCreatedChatId: pendingCreatedIdRef.current,
    effectiveInitialMessages,
    pendingPersistenceCount,
    invalidateQueue,
    setMessages,
    resetPersistenceTracking,
  });

  // draft の新規保存と、保存済み chat の差分更新を振り分ける。
  const handlePersistence = async (currentMessages: AgentUIMessage[]) => {
    // server ID があれば差分更新し、なければ draft を新規作成する。
    if (persistedChatId) {
      await updateChat(persistedChatId, currentMessages);
    } else {
      if (isCreating) return; // 同じ draft の create mutation が競合するのを防ぐ。
      await createChat(currentMessages);
    }
  };
  const latestHandlePersistenceRef = useRef(handlePersistence);
  useEffect(() => {
    latestHandlePersistenceRef.current = handlePersistence;
  });

  /**
   * 利用者が明示的に停止した stream は、AI SDK の「生成済み内容を保持する」契約に合わせて保存する。
   *
   * @remarks
   * 通信切断や stream error は従来どおり rollback する。途中の Tool part も GraphQL へ保存でき、
   * 次回 model input では `ignoreIncompleteToolCalls` により未完了 call が除外される。
   */
  async function persistIntentionallyStoppedStream(
    currentMessages: AgentUIMessage[],
  ) {
    const stoppedMessages = markLatestAssistantAsUserStopped(currentMessages);
    if (stoppedMessages === currentMessages) {
      setMessages(latestPersistedMessagesRef.current);
      return;
    }

    const persistableMessages = stripTransientFilePartsFromMessages(
      withClientMessageIds(filterEmptyAssistantMessages(stoppedMessages)),
    );
    const hasStoppedAssistant = persistableMessages.some(
      (message) =>
        message.role === "assistant" &&
        message.metadata?.completionCause === "user-stopped" &&
        message.parts.length > 0,
    );
    if (!hasStoppedAssistant) {
      setMessages(latestPersistedMessagesRef.current);
      return;
    }

    setMessages(persistableMessages);
    await handlePersistence(persistableMessages);
  }

  /**
   * 明示停止を通知した経路にかかわらず、生成済み内容の保存と待機解除を同じ順序で行う。
   */
  async function persistStoppedStreamAndSettleWait(
    currentMessages: AgentUIMessage[],
    token: PersistenceGenerationToken,
  ) {
    try {
      await persistIntentionallyStoppedStream(currentMessages);
      resolveNextWait(token);
    } catch (persistenceError) {
      const normalizedError = toError(persistenceError);
      const operation = getPersistenceOperation(normalizedError);
      logger.error(normalizedError, {
        ...logContext,
        source: "ai-chat",
        ...(operation ? { operation } : {}),
      });
      setExternalError(null);
      setError(normalizedError.message);
      dispatch({ type: "ERROR" });
      rejectNextWait(token, normalizedError);
    }
  }

  const createChat = async (currentMessages: AgentUIMessage[]) => {
    dispatch({ type: "START_CREATE" });
    try {
      const persistableMessages =
        stripTransientFilePartsFromMessages(currentMessages);
      const firstUserMessage = currentMessages.find((m) => m.role === "user");
      const firstTextTitle =
        firstUserMessage?.parts?.[0]?.type === "text"
          ? firstUserMessage.parts[0].text.trim().slice(0, 40)
          : "";
      const title = firstTextTitle || "New Chat";

      const res = await createMutation({
        input: {
          organizationID: workspaceId,
          title,
          shared: false,
          messages: persistableMessages.map((m) => uiMessageVercelToGql(m)),
        },
      });

      if (res.error) throw new Error(res.error.message);

      const newId = res.data?.createAgentChat?.id;
      if (!newId) {
        throw new Error("Failed to create chat");
      }

      const returnedMessages = res.data?.createAgentChat?.messages ?? [];
      returnedMessages.forEach((message) => {
        const clientMessageId = getClientMessageIdFromMetadata(
          message.metadata,
        );
        if (clientMessageId && message.id) {
          serverMessageIdMap.current.set(clientMessageId, message.id);
        }
      });

      // draft 世代に結び付いた未実行の送信を、新しい server chat へ持ち越さない。
      markContextTransition();
      pendingCreatedIdRef.current = newId;
      // create response と対応する client message を保存済みとして記録する。
      currentMessages.forEach((m) => {
        const clientId = getClientMessageId(m);
        persistedMessageIds.current.add(clientId);
        persistedSnapshots.current.set(clientId, snapshotMessage(m));
      });
      latestPersistedMessagesRef.current = persistableMessages;

      dispatch({ type: "CREATED", newId });
      onCreate(newId);
    } catch (e) {
      const normalizedError = toError(e);
      setAppError(null);
      setExternalError(null);
      setError(i18n._(msg`Failed to save chat: ${normalizedError.message}`));
      dispatch({ type: "ERROR" });
      throw new ChatPersistenceError("createAgentChat", normalizedError);
    }
  };

  const updateChat = async (
    chatId: string,
    currentMessages: AgentUIMessage[],
  ) => {
    // client ID と保存済み snapshot から、追加分と更新分を分離する。
    const pendingNew = currentMessages.filter((m) => {
      if (m.role === "system") return false;
      const clientId = getClientMessageId(m);
      return !persistedMessageIds.current.has(clientId);
    });

    const pendingUpdates = currentMessages.filter((m) => {
      if (m.role === "system") return false;
      const clientId = getClientMessageId(m);
      const prev = persistedSnapshots.current.get(clientId);
      return (
        persistedMessageIds.current.has(clientId) &&
        prev !== undefined &&
        prev !== snapshotMessage(m)
      );
    });
    try {
      // 未保存メッセージをまとめて追加する。
      if (pendingNew.length > 0) {
        const persistablePendingNew =
          stripTransientFilePartsFromMessages(pendingNew);
        const res = await appendMutation({
          input: {
            id: chatId,
            messages: persistablePendingNew.map((m) => uiMessageVercelToGql(m)),
          },
        });

        if (res.error) throw new Error(res.error.message);

        const returnedMessages =
          res.data?.appendAgentChatMessages?.messages?.map(
            (m) => uiMessageGqlToVercel(m) as AgentUIMessage,
          ) ?? [];

        const updateMap = (list: AgentUIMessage[]) => {
          list.forEach((m) => {
            const clientId = getClientMessageId(m);
            if (m.id) serverMessageIdMap.current.set(clientId, m.id);
            persistedMessageIds.current.add(clientId);
            persistedSnapshots.current.set(clientId, snapshotMessage(m));
          });
        };

        updateMap(returnedMessages.length > 0 ? returnedMessages : pendingNew);
      }

      // 保存後に Tool receipt などが変化したメッセージを個別更新する。
      for (const m of pendingUpdates) {
        const clientId = getClientMessageId(m);
        const serverId = serverMessageIdMap.current.get(clientId);
        if (!serverId) {
          throw new Error(
            `Missing server message ID for changed chat message: ${clientId}`,
          );
        }
        const persistableMessage = stripTransientFilePartsFromMessages([m])[0];

        const res = await updateMessageMutation({
          input: {
            id: serverId,
            message: uiMessageVercelToGql(persistableMessage ?? m),
          },
        });

        if (res.error) throw new Error(res.error.message);
        persistedSnapshots.current.set(clientId, snapshotMessage(m));
      }
      latestPersistedMessagesRef.current =
        stripTransientFilePartsFromMessages(currentMessages);
    } catch (e) {
      const normalizedError = toError(e);
      setAppError(null);
      setExternalError(null);
      setError(normalizedError.message);
      throw new ChatPersistenceError("updateAgentChat", normalizedError);
    }
  };

  const sendMessageAndWaitForPersistence = useCallback(
    (...args: Parameters<typeof sendMessage>) => {
      const [message, options] = args;
      return runSerialized(callbackGenerationToken, async () => {
        if (hasPendingToolApproval(latestMessagesRef.current)) {
          resolveNextWait(callbackGenerationToken);
          return;
        }
        try {
          await latestSendMessageRef.current(
            message,
            buildLatestRequestOptions(options),
          );
        } catch (error) {
          const normalizedError = toError(error);
          if (
            isAbortLikeError(normalizedError) &&
            isSameGenerationToken(
              stopRequestedTokenRef.current,
              callbackGenerationToken,
            )
          ) {
            // onFinish が受け取る throttle 前のlive snapshotで保存し、waitを解放する。
            return;
          }
          clearStopRequestForToken(callbackGenerationToken);
          throw normalizedError;
        }
      }).finally(() => {
        clearStopRequestForToken(callbackGenerationToken);
      });
    },
    [
      buildLatestRequestOptions,
      callbackGenerationToken,
      clearStopRequestForToken,
      isSameGenerationToken,
      resolveNextWait,
      runSerialized,
    ],
  );

  /** legacy confirmation の結果を即座に readiness 判定へ反映する。 */
  const addToolOutputAndTrack = useCallback(
    async (...args: Parameters<typeof addToolOutput>) => {
      const [result] = args;
      await latestAddToolOutputRef.current(...args);

      const currentMessages = latestMessagesRef.current;
      const latestMessage = currentMessages.at(-1);
      if (latestMessage?.role !== "assistant") return;

      const updatedMessages = [
        ...currentMessages.slice(0, -1),
        {
          ...latestMessage,
          parts: latestMessage.parts.map((part) => {
            if (!isToolUIPart(part) || part.toolCallId !== result.toolCallId) {
              return part;
            }
            // AI SDK の addToolOutput と同じ状態遷移を readiness 用 snapshot に反映する。
            return (
              result.state === "output-error"
                ? {
                    ...part,
                    state: "output-error" as const,
                    errorText: result.errorText,
                  }
                : {
                    ...part,
                    state: "output-available" as const,
                    output: result.output,
                  }
            ) as typeof part;
          }),
        },
      ];
      latestMessagesRef.current = updatedMessages;
      setMessages(updatedMessages);
    },
    [setMessages],
  );

  /**
   * 同じ chat 世代で要求された approval continuation を一つの実行にまとめる。
   *
   * 各 approval response は個別に直列化・保存する一方、その後の continuation は
   * batch の未完了期間から実行完了まで、すべての呼び出し元で同じ Promise を共有する。
   * chat 世代が変われば以前の Promise は再利用せず、成功・失敗のどちらでも参照を
   * 解放して、利用者による明示的な再試行を可能にする。
   */
  const requestApprovalContinuation = useCallback(() => {
    let continuation = approvalContinuationRef.current;
    if (
      !continuation ||
      !isSameGenerationToken(continuation.token, callbackGenerationToken)
    ) {
      continuation = createApprovalContinuationWait(callbackGenerationToken);
      approvalContinuationRef.current = continuation;
    }

    const isReady = isApprovalContinuationReady(
      latestMessagesRef.current,
      LEGACY_CONFIRMATION_TOOL_NAMES,
    );
    if (!continuation.started && isReady) {
      continuation.started = true;
      const activeContinuation = continuation;
      void sendMessageAndWaitForPersistence().then(
        activeContinuation.resolve,
        activeContinuation.reject,
      );
      const clear = () => {
        if (approvalContinuationRef.current === activeContinuation) {
          approvalContinuationRef.current = null;
        }
      };
      void activeContinuation.promise.then(clear, clear);
    }

    return continuation.promise;
  }, [
    callbackGenerationToken,
    isSameGenerationToken,
    sendMessageAndWaitForPersistence,
  ]);

  /**
   * 承認応答を永続化してから continuation を許可する。
   * 保存に失敗した場合は approval-requested の snapshot へ戻し、未保存の承認で
   * mutation が進まないようにする。
   */
  const addToolApprovalResponseAndWaitForPersistence = useCallback(
    async (...args: Parameters<typeof addToolApprovalResponse>) => {
      const [response] = args;
      await runSerialized(callbackGenerationToken, async () => {
        const previousMessages = latestMessagesRef.current;
        const approvedMessages = applyToolApprovalResponse(
          previousMessages,
          response,
        );

        await latestAddToolApprovalResponseRef.current(response);
        latestMessagesRef.current = approvedMessages;
        setMessages(approvedMessages);

        try {
          await latestHandlePersistenceRef.current(approvedMessages);
          resolveNextWait(callbackGenerationToken);
        } catch (error) {
          latestMessagesRef.current = previousMessages;
          setMessages(previousMessages);
          const normalizedError = toError(error);
          const operation = getPersistenceOperation(normalizedError);
          logger.error(normalizedError, {
            ...logContext,
            source: "ai-chat",
            ...(operation ? { operation } : {}),
          });
          setAppError(null);
          setExternalError(null);
          setError(
            latestI18nRef.current._(
              msg`Failed to save approval response: ${normalizedError.message}`,
            ),
          );
          throw normalizedError;
        }
      });
    },
    [
      callbackGenerationToken,
      logContext,
      resolveNextWait,
      runSerialized,
      setMessages,
    ],
  );

  const latestAssistantParts =
    messages.at(-1)?.role === "assistant" ? messages.at(-1)?.parts : undefined;
  const retryableApprovalPart = latestAssistantParts?.find(
    (part) => isToolUIPart(part) && part.state === "approval-responded",
  );
  const retryableApprovalToolCallId =
    retryableApprovalPart && isToolUIPart(retryableApprovalPart)
      ? retryableApprovalPart.toolCallId
      : undefined;
  const persistedAssistantParts =
    effectiveInitialMessages.at(-1)?.role === "assistant"
      ? effectiveInitialMessages.at(-1)?.parts
      : undefined;
  const hasPersistedRetryableApproval = persistedAssistantParts?.some(
    (part) =>
      isToolUIPart(part) &&
      part.state === "approval-responded" &&
      part.toolCallId === retryableApprovalToolCallId,
  );
  const isContinuationReady = isApprovalContinuationReady(
    messages,
    LEGACY_CONFIRMATION_TOOL_NAMES,
  );
  const canRetryApprovalContinuation =
    retryableApprovalToolCallId !== undefined &&
    isEditable &&
    isContinuationReady &&
    (status === "error" ||
      (status === "ready" && hasPersistedRetryableApproval === true));

  /** 保存済みの全 approval response が揃った場合だけ continuation を再送する。 */
  const retryApprovalContinuation = useCallback(async () => {
    if (!canRetryApprovalContinuation || approvalContinuationRetryRef.current) {
      return false;
    }

    approvalContinuationRetryRef.current = true;
    setIsRetryingApprovalContinuation(true);
    clearError();
    try {
      await requestApprovalContinuation();
      return true;
    } finally {
      approvalContinuationRetryRef.current = false;
      setIsRetryingApprovalContinuation(false);
    }
  }, [canRetryApprovalContinuation, clearError, requestApprovalContinuation]);

  const startNewChat = useCallback(() => {
    const emptyMessages: AgentUIMessage[] = [];
    startNewChatContextRef.current = {
      chatId: propActiveChatId ?? null,
      messageSignature: buildMessagesSignature(effectiveInitialMessages),
    };
    setIsStartingNewChat(true);
    clearError();
    setMessages(emptyMessages);
    resetPersistenceTracking(emptyMessages);
    pendingCreatedIdRef.current = null;
    dispatch({ type: "SYNC_PROP", chatId: null });
    invalidateQueue(new Error("Chat reset"));
    return true;
  }, [
    clearError,
    effectiveInitialMessages,
    invalidateQueue,
    propActiveChatId,
    resetPersistenceTracking,
    setMessages,
  ]);

  // 送信可能条件を一か所で検証してから AI SDK の sendMessage へ渡す。
  const handleSend = useCallback(
    async ({ text, mentions, attachments }: ChatSendInput) => {
      if (!text.trim() && (!attachments || attachments.length === 0)) {
        return false;
      }
      if (!isEditable) {
        setAppError(null);
        setExternalError(null);
        setError(i18n._(msg`Only the creator can send messages`));
        return false;
      }
      if (hasPendingToolApproval(latestMessagesRef.current)) {
        return false;
      }
      if (isRestrictedHighTierModel(model, aiModelAccess)) {
        setExternalError(null);
        setError(null);
        setAppError(
          new ChatAppError({
            code: "AI_MODEL_RESTRICTED",
            message: i18n._(
              msg`This model requires AI PRO or AI MAX. Please switch models or contact us to upgrade.`,
            ),
          }),
        );
        return false;
      }
      clearError();
      const isNewChat = !(persistedChatId ?? propActiveChatId);
      const attachmentOnlyTitle =
        attachments && attachments.length > 0 && !text.trim()
          ? `Attached ${Math.min(attachments.length, CHAT_ATTACHMENT_MAX_COUNT)} file${attachments.length === 1 ? "" : "s"}`
          : text;
      const message =
        attachments && attachments.length > 0
          ? {
              parts: buildChatMessageParts({
                text: attachmentOnlyTitle,
                attachments,
              }),
            }
          : { text };
      try {
        await sendMessageAndWaitForPersistence(message, {
          body: buildRequestBody(persistedChatId, { text, mentions }),
        });
        if (isNewChat) {
          trackEvent({
            eventName: "ai_chat_started",
            payload: {},
          });
        }
        return true;
      } catch (caught) {
        if (ChatAppError.isInstance(caught)) {
          setAppError(caught);
          setExternalError(null);
          setError(null);
          return false;
        }

        if (APICallError.isInstance(caught)) {
          setAppError(null);
          setExternalError(resolveChatExternalError(caught));
          setError(null);
          return false;
        }

        // chat 切替・リセットによる queue 無効化は正常フローなので swallow する。
        if (isQueueInvalidationError(caught)) {
          return false;
        }

        if (isNetworkTransportError(caught)) {
          setAppError(null);
          setExternalError(resolveChatExternalError(caught));
          setError(null);
          return false;
        }

        throw caught;
      }
    },
    [
      aiModelAccess,
      buildRequestBody,
      clearError,
      i18n,
      isEditable,
      model,
      persistedChatId,
      propActiveChatId,
      sendMessageAndWaitForPersistence,
      trackEvent,
    ],
  );

  const derivedUsage = useMemo(() => getLatestTokenUsage(messages), [messages]);

  return {
    messages,
    status,
    stop,
    addToolResult: addToolOutputAndTrack,
    addToolApprovalResponse: addToolApprovalResponseAndWaitForPersistence,
    canRetryApprovalContinuation,
    isRetryingApprovalContinuation,
    retryApprovalContinuation,
    retryableApprovalToolCallId,
    sendMessage: sendMessageAndWaitForPersistence,
    requestApprovalContinuation,
    handleSend,
    startNewChat,
    isEditable,
    canSendMessages: isEditable,
    appError,
    externalError,
    error,
    clearError,
    usage: derivedUsage,
    activeChatId: persistedChatId, // Expose this so the UI knows if we are draft or not
    isCreating, // Expose creation status
    isPersistingMessages: pendingPersistenceCount > 0,
  };
}
